import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import "./PerformanceDashboard.css";

const NUMERIC_KEYPAD_KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  [".", "0", "⌫"],
];

const NumericKeypadModal = ({ show, title, value, onConfirm, onCancel }) => {
  const [buffer, setBuffer] = useState("");

  useEffect(() => {
    if (show) setBuffer(value === undefined || value === null ? "" : String(value));
  }, [show, value]);

  const handleKey = (k) => {
    if (k === "⌫") {
      setBuffer((s) => s.slice(0, -1));
      return;
    }
    if (k === ".") {
      if (buffer.includes(".")) return;
      if (buffer === "") {
        setBuffer("0.");
        return;
      }
    }
    if (buffer === "0" && k !== ".") {
      setBuffer(k);
      return;
    }
    setBuffer((s) => s + k);
  };

  const handleClear = () => setBuffer("");
  const handleConfirm = () => {
    const v = buffer.trim() === "" ? "" : buffer.replace(/^0+(\d)/, "$1");
    onConfirm(v);
    onCancel();
  };

  if (!show) return null;

  return (
    <div className="numeric-keypad-overlay" onClick={onCancel}>
      <div className="numeric-keypad-modal" onClick={(e) => e.stopPropagation()}>
        <div className="numeric-keypad-header">
          <span className="numeric-keypad-title">{title}</span>
          <button type="button" className="numeric-keypad-close" onClick={onCancel} aria-label="Fermer">
            ×
          </button>
        </div>
        <div className="numeric-keypad-display">{buffer || "0"}</div>
        <div className="numeric-keypad-grid">
          {NUMERIC_KEYPAD_KEYS.map((row, i) => (
            <div key={i} className="numeric-keypad-row">
              {row.map((k) => (
                <button key={k} type="button" className="numeric-keypad-key" onClick={() => handleKey(k)}>
                  {k}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="numeric-keypad-actions">
          <button type="button" className="numeric-keypad-btn numeric-keypad-btn-clear" onClick={handleClear}>
            C
          </button>
          <button type="button" className="numeric-keypad-btn numeric-keypad-btn-ok" onClick={handleConfirm}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

function PerformanceDashboard() {
  const [performanceData, setPerformanceData] = useState({
    date: new Date().toISOString().split('T')[0],
    lastClosureRevenue: {
      espece: 0,
      tpe: 0,
      ticketResto: 0,
      glovo: 0,
      yassir: 0,
      total: 0,
      closureDate: "",
      closureType: ""
    },
    expenses: [],
    allocations: [],
    dailyPerformance: {
      revenue: 0,
      expensesPaid: 0,
      netProfit: 0,
      profitMargin: 0
    },
    weeklySummary: {
      totalRevenue: 0,
      totalExpensesPaid: 0,
      totalNetProfit: 0,
      remainingExpenses: 0,
      profitMargin: 0
    },
    supplierPayments: [],
    weeklySupplierPayments: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expenseInputs, setExpenseInputs] = useState({});
  const [supplierInputs, setSupplierInputs] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  const [keypad, setKeypad] = useState({ show: false, type: null, id: null, title: "" });

  // 🔥 État pour stocker la localisation de l'utilisateur
  const [userlocation, setUserlocation] = useState(null);
  const [userLocationName, setUserLocationName] = useState("");
  const [locations, setLocations] = useState([]); // Pour les admins

  const authState = useSelector(state => state.AuthReducer);
  const user = authState?.user;

  // 🔥 Configuration axios avec authentification
  const axiosInstance = axios.create({
    baseURL: "/",
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  // 🔥 Fonction pour formater les montants
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return "0.00 DT";
    }
    const num = parseFloat(amount);
    return isNaN(num) ? "0.00 DT" : `${num.toFixed(2)} DT`;
  };

  // 🔥 Initialiser la localisation de l'utilisateur
  useEffect(() => {
    const initUserLocation = () => {
      if (user) {
        // Vérifier si l'utilisateur est admin
        const role = user.role?.toLowerCase();
        const isUserAdmin = role?.includes("admin");
        setIsAdmin(isUserAdmin);

        // Pour les non-admins, définir la localisation
        if (!isUserAdmin && user.localisation) {
          const location = user.localisation._id || user.localisation;
          const locationName = user.localisation.name || "Localisation inconnue";

          setUserlocation(location);
          setUserLocationName(locationName);

          console.log(`📍 Localisation utilisateur initialisée: ${locationName} (${location})`);
        } else if (isUserAdmin) {
          // For admins, clear location par défaut (toutes les locations)
          setUserlocation("all");
          setUserLocationName("Toutes les locations");
          console.log("👤 Utilisateur admin - accès à toutes les localisations");
        } else {
          // Non-admin without location
          setUserlocation(null);
          setUserLocationName("");
          console.log("⚠️ Utilisateur sans localisation assignée");
        }
      } else {
        // No user, clear location
        setUserlocation(null);
        setUserLocationName("");
      }
    };

    initUserLocation();
  }, [user]);

  // 🔥 Charger toutes les locations si admin
  useEffect(() => {
    if (isAdmin) {
      const fetchLocations = async () => {
        try {
          const res = await axiosInstance.get("/api/Locationspin/getAllLocations");
          if (res.data) setLocations(res.data);
        } catch (err) {
          console.log("ℹ️ Erreur chargement locations:", err);
        }
      };
      fetchLocations();
    }
  }, [isAdmin]);

  // 🔥 Gérer le changement de location pour les admins
  const handleLocationChange = (e) => {
    const locId = e.target.value;
    setUserlocation(locId);

    if (locId === "all") {
      setUserLocationName("Toutes les locations");
    } else if (locId) {
      const locName = locations.find(l => l._id === locId)?.name || "Localisation";
      setUserLocationName(locName);
    } else {
      setUserLocationName("");
    }

    // Recharger les données avec la nouvelle location
    loadPerformanceData(selectedDate, locId === "all" ? null : locId);
  };

  // 🔥 Calculer le début de la semaine (lundi à 00:00:00)
  const calculateWeekStart = (dateStr) => {
    if (!dateStr) return "";
    const start = new Date(dateStr);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    return start.toISOString().split('T')[0];
  };

  const weekStartDate = calculateWeekStart(selectedDate);

  // 🔥 Recettes du jour = dernière journée clôturée (fin de journée), par localisation.
  // On affiche la veille ou la dernière date qui a une clôture "fin_journee", pas la date sélectionnée.
  // — Uniquement "fin_journee", ignorer "passation".
  const fetchLastClosureDetails = async (locationFilter = null) => {
    try {
      console.log("🔍 Recherche dernière journée clôturée (fin de journée), location:", locationFilter ?? "toutes");

      const params = {};
      if (locationFilter && locationFilter !== "all") {
        params.locationId = locationFilter;
      }

      const closuresRes = await axiosInstance.get("/api/CloseDay/getAllClosures", { params });
      const closures = closuresRes.data || [];

      if (closures.length === 0) {
        console.log("ℹ️ Aucune clôture trouvée");
        return null;
      }

      const getClosureLocation = (c) =>
        c.locationId?.toString() ||
        (c.localisation && (typeof c.localisation === 'object' ? c.localisation._id?.toString() : c.localisation)) ||
        null;

      const type = (c) => (c.closureType || "").toString().toLowerCase().trim();

      // Uniquement "fin_journee", exclure "passation"
      const finJourneeOnly = closures.filter((c) => {
        if (!c.date) return false;
        const t = type(c);
        if (t === "passation") return false;
        return t === "fin_journee";
      });

      // Filtrer par location
      let relevant = finJourneeOnly;
      if (locationFilter && locationFilter !== "all") {
        relevant = finJourneeOnly.filter((c) => {
          const loc = getClosureLocation(c);
          return loc && loc.toString() === locationFilter.toString();
        });
      }

      if (relevant.length === 0) {
        console.log(`ℹ️ Aucune clôture fin de journée pour cette location`);
        return null;
      }

      // Dernière journée clôturée = date max parmi les clôtures
      const maxDate = relevant.reduce((max, c) => {
        const d = new Date(c.date).getTime();
        return d > max ? d : max;
      }, 0);
      const lastClosedDateStr = new Date(maxDate).toISOString().split('T')[0];

      // Garder uniquement les clôtures de cette dernière journée
      const lastDayClosures = relevant.filter((c) => {
        const d = new Date(c.date).toISOString().split('T')[0];
        return d === lastClosedDateStr;
      });

      // Une clôture par location (la plus récente si doublon)
      const byLocation = {};
      lastDayClosures.forEach((c) => {
        const loc = getClosureLocation(c) || "unknown";
        const existing = byLocation[loc];
        const cTime = new Date(c.date).getTime();
        if (!existing || cTime > new Date(existing.date).getTime()) {
          byLocation[loc] = c;
        }
      });

      const lastPerLocation = Object.values(byLocation);

      const revenueByMethod = { Espece: 0, TPE: 0, "T.Resto": 0, Glovo: 0, Yassir: 0, Autre: 0 };
      let totalSalesSum = 0;

      lastPerLocation.forEach((c) => {
        const paymentStats = c.paymentStats || {};
        Object.entries(paymentStats).forEach(([method, amount]) => {
          const norm = normalizePaymentMethod(method);
          if (revenueByMethod[norm] !== undefined) {
            revenueByMethod[norm] += parseFloat(amount) || 0;
          } else {
            revenueByMethod["Autre"] += parseFloat(amount) || 0;
          }
        });
        totalSalesSum += parseFloat(c.totalSales) || 0;
      });

      const byMethodTotal = Object.values(revenueByMethod).reduce((a, b) => a + b, 0);
      const total = totalSalesSum > 0 ? totalSalesSum : byMethodTotal;
      const closureType =
        !locationFilter || locationFilter === "all"
          ? "🏁 FIN DE JOURNÉE (toutes locations)"
          : "🏁 FIN DE JOURNÉE";

      const lastClosureRevenue = {
        espece: revenueByMethod.Espece,
        tpe: revenueByMethod.TPE,
        ticketResto: revenueByMethod["T.Resto"],
        glovo: revenueByMethod.Glovo,
        yassir: revenueByMethod.Yassir,
        total,
        closureDate: lastClosedDateStr,
        closureType
      };

      console.log("💰 Recettes du jour (dernière journée clôturée):", lastClosureRevenue);
      return lastClosureRevenue;
    } catch (err) {
      console.error("❌ Erreur récupération clôture:", err);
      return null;
    }
  };

  // 🔥 Normaliser les méthodes de paiement
  const normalizePaymentMethod = (method) => {
    if (!method) return "Autre";

    const methodStr = method.toString().toLowerCase().trim();

    if (methodStr.includes("espece") || methodStr.includes("espèce")) return "Espece";
    if (methodStr.includes("tpe") || methodStr.includes("carte") || methodStr.includes("cb")) return "TPE";
    if (methodStr.includes("resto") || methodStr.includes("ticket")) return "T.Resto";
    if (methodStr.includes("glovo")) return "Glovo";
    if (methodStr.includes("yassir")) return "Yassir";

    return "Autre";
  };

  // 🔥 CORRIGÉ: Récupérer les dépenses pour la location de l'utilisateur
  const fetchExpenses = async (locationFilter = null) => {
    try {
      console.log("🔄 Chargement des dépenses...");

      // 🔥 Construire les paramètres avec location
      const params = {};

      // Appliquer le filtre de location
      if (locationFilter !== null) {
        params.location = locationFilter;
      } else if (!isAdmin && userlocation && userlocation !== "all") {
        // Pour les non-admins, filtrer par leur localisation
        params.location = userlocation;
      } else if (isAdmin && userlocation && userlocation !== "all") {
        // Pour les admins avec filtre spécifique
        params.location = userlocation;
      }

      console.log("📋 Paramètres requête dépenses:", params);

      const response = await axiosInstance.get("/api/Expenses/getAllExpenses", { params });

      if (response.data.success) {
        let allExpenses = response.data.expenses || [];

        console.log(`📊 ${allExpenses.length} dépenses récupérées`);

        // 🔥 FILTRAGE SUPPLÉMENTAIRE côté client pour sécurité
        if ((!isAdmin && userlocation && userlocation !== "all") ||
          (isAdmin && locationFilter && locationFilter !== "all")) {
          const filteredExpenses = allExpenses.filter(expense => {
            const expenselocation = expense.location || expense.localisation;

            // Pour le débogage
            if (!expenselocation) {
              console.warn(`⚠️ Dépense "${expense.name}" sans location`);
              return false;
            }

            const isSameLocation = expenselocation.toString() === (locationFilter || userlocation).toString();

            if (!isSameLocation) {
              console.log(`❌ Dépense "${expense.name}" location: ${expenselocation} ≠ filtre: ${locationFilter || userlocation}`);
            }

            return isSameLocation;
          });

          console.log(`📍 ${filteredExpenses.length} dépenses filtrées pour ${userLocationName}`);
          return filteredExpenses;
        }

        return allExpenses;
      }
      return [];
    } catch (err) {
      console.error("❌ Erreur chargement dépenses:", err);
      return getDefaultExpenses();
    }
  };

  // 🔥 CORRIGÉ: Récupérer les paiements fournisseurs cumulés de la semaine
  const fetchWeeklySupplierPayments = async (startDate, endDate, locationFilter = null) => {
    try {
      console.log("📅 Récupération paiements fournisseurs hebdo:", { startDate, endDate });

      const params = { startDate, endDate };

      // Appliquer le filtre de location
      if (locationFilter !== null) {
        params.location = locationFilter;
      } else if (!isAdmin && userlocation && userlocation !== "all") {
        params.location = userlocation;
      } else if (isAdmin && userlocation && userlocation !== "all") {
        params.location = userlocation;
      }

      const response = await axiosInstance.get("/api/SupplierPayments/getWeeklySummary", { params });

      if (response.data.success) {
        return response.data.weeklyPayments || [];
      }
      return [];
    } catch (err) {
      console.error("❌ Erreur récupération paiements fournisseurs hebdo:", err);
      return [];
    }
  };

  // 🔥 CORRIGÉ: Récupérer les allocations régulières cumulées de la semaine
  const fetchWeeklyRegularAllocations = async (startDate, endDate, locationFilter = null) => {
    try {
      console.log("📅 Récupération allocations régulières hebdo:", { startDate, endDate });

      const params = { startDate, endDate };

      // Appliquer le filtre de location
      if (locationFilter !== null) {
        params.locationId = locationFilter;
      } else if (!isAdmin && userlocation && userlocation !== "all") {
        params.locationId = userlocation;
      } else if (isAdmin && userlocation && userlocation !== "all") {
        params.locationId = userlocation;
      }

      console.log("📋 Paramètres allocations hebdo:", params);

      const response = await axiosInstance.get("/api/Allocations/getWeeklyAllocations", { params });

      if (response.data.success) {
        console.log(`✅ ${response.data.allocations?.length || 0} allocations récupérées`);
        return response.data.allocations || [];
      }
      return [];
    } catch (err) {
      console.error("❌ Erreur récupération allocations régulières hebdo:", err);
      return [];
    }
  };

  // 🔥 CORRIGÉ: Charger toutes les données avec filtrage par location
  const loadPerformanceData = async (date, locationFilter = null) => {
    try {
      setLoading(true);
      setError("");

      console.log(`🚀 Chargement données pour ${date}`);
      console.log(`👤 Utilisateur: ${user?.name}, Admin: ${isAdmin}, Location: ${userLocationName}`);
      console.log(`📍 Filtre location: ${locationFilter || userlocation || "aucun"}`);

      // 1. Calculer le début de la semaine
      const weekStart = calculateWeekStart(date);

      // Déterminer la location à utiliser pour le filtrage
      const effectiveLocation = locationFilter !== null ?
        (locationFilter === "all" ? null : locationFilter) :
        (userlocation === "all" ? null : userlocation);

      // 2. Charger la dernière clôture POUR LA LOCATION DE L'UTILISATEUR
      let lastClosureRevenue = {
        espece: 0,
        tpe: 0,
        ticketResto: 0,
        glovo: 0,
        yassir: 0,
        total: 0,
        closureDate: "",
        closureType: ""
      };

      try {
        const closureRevenue = await fetchLastClosureDetails(effectiveLocation);
        if (closureRevenue) {
          lastClosureRevenue = closureRevenue;
          console.log(`✅ Dernière journée clôturée: ${lastClosureRevenue.closureDate} pour location: ${effectiveLocation || "toutes"}`);
        } else {
          console.log(`ℹ️ Aucune clôture fin de journée pour location: ${effectiveLocation || "toutes"}`);
        }
      } catch (err) {
        console.log("ℹ️ Impossible de récupérer la clôture:", err);
      }

      // 3. Charger les dépenses POUR LA LOCATION DE L'UTILISATEUR
      let allExpenses = await fetchExpenses(effectiveLocation);

      console.log(`📊 ${allExpenses.length} dépenses chargées pour location: ${effectiveLocation || "toutes"}`);

      // 4. Séparer dépenses régulières vs fournisseurs
      const regularExpenses = [];
      const supplierExpenses = [];

      allExpenses.forEach(expense => {
        const category = (expense.category || "").toLowerCase();
        const name = (expense.name || "").toLowerCase();

        if (category.includes("fournisseur") || category.includes("supplier") ||
          name.includes("fournisseur") || name.includes("supplier")) {
          supplierExpenses.push(expense);
        } else {
          regularExpenses.push(expense);
        }
      });

      console.log(`📈 ${regularExpenses.length} dépenses régulières, ${supplierExpenses.length} dépenses fournisseurs`);

      // 5. Récupérer les allocations du jour POUR LA LOCATION
      let existingAllocations = [];
      let expenseSummaries = [];
      try {
        const params = { date };

        // 🔥 API attend locationId (pas location)
        if (effectiveLocation && effectiveLocation !== "all") {
          params.locationId = effectiveLocation;
        }

        const allocRes = await axiosInstance.get("/api/Allocations/getDailyAllocationsSummary", { params });
        if (allocRes.data.success) {
          existingAllocations = allocRes.data.allocations || [];
          expenseSummaries = allocRes.data.expenseSummaries || [];

          console.log(`✅ ${existingAllocations.length} allocations trouvées pour ${date} (location: ${effectiveLocation || "toutes"})`);
        }
      } catch (err) {
        console.log("ℹ️ Aucune allocation pour cette date:", err);
      }

      // 6. Récupérer les paiements fournisseurs et réguliers de la SEMAINE POUR LA LOCATION
      let weeklySupplierPayments = [];
      let weeklyRegularAllocations = [];
      try {
        const [supplierRes, regularRes] = await Promise.all([
          fetchWeeklySupplierPayments(weekStart, date, effectiveLocation),
          fetchWeeklyRegularAllocations(weekStart, date, effectiveLocation)
        ]);
        weeklySupplierPayments = supplierRes;
        weeklyRegularAllocations = regularRes;
        console.log(`✅ ${weeklySupplierPayments.length} paiements fournisseurs, ${weeklyRegularAllocations.length} allocations régulières hebdo (location: ${effectiveLocation || "toutes"})`);
      } catch (err) {
        console.log("ℹ️ Erreur chargement cumuls hebdomadaires:", err);
      }

      // 7. Calculer le total déjà alloué aujourd'hui (espèces)
      const totalAllocatedToday = existingAllocations.reduce((sum, alloc) =>
        sum + (parseFloat(alloc.amount) || 0), 0
      );

      // 8. Calculer les dépenses régulières avec cumul HEBDOMADAIRE
      const expensesWithUpdatedAmounts = regularExpenses.map(expense => {
        // Trouver les allocations de la semaine pour cette dépense
        const weeklyAllocationsForExpense = weeklyRegularAllocations.filter(
          a => a.expenseId === expense._id || a.expenseId?._id === expense._id
        );

        // Total payé cette semaine
        const totalPaidThisWeek = weeklyAllocationsForExpense.reduce(
          (sum, alloc) => sum + (parseFloat(alloc.amount) || 0), 0
        );

        // Déjà payé aujourd'hui (pour info)
        const totalAllocatedTodayForExpense = existingAllocations
          .filter(a => a.expenseId === expense._id || a.expenseId?._id === expense._id)
          .reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);

        const expenseAmount = parseFloat(expense.amount) || 0;
        const previouslyPaid = parseFloat(expense.previouslyPaid) || 0;

        // Le montant restant doit refléter tout ce qui a été payé historiquement + cette semaine
        const remainingAmount = Math.max(0, expenseAmount - previouslyPaid);

        return {
          ...expense,
          amount: expenseAmount,
          previouslyPaid: previouslyPaid,
          totalPaidThisWeek: parseFloat(totalPaidThisWeek.toFixed(2)),
          totalAllocatedToday: parseFloat(totalAllocatedTodayForExpense.toFixed(2)),
          remainingAmount: parseFloat(remainingAmount.toFixed(2)),
          isFullyPaid: remainingAmount <= 0.01
        };
      });

      // 9. Calculer les paiements fournisseurs avec cumul HEBDOMADAIRE
      const supplierExpensesWithAmounts = supplierExpenses.map(expense => {
        // Trouver les paiements de la semaine pour ce fournisseur
        const weeklyPaymentsForSupplier = weeklySupplierPayments.filter(
          p => p.expenseId === expense._id || p.expenseId?._id === expense._id
        );

        // Total payé cette semaine
        const totalPaidThisWeek = weeklyPaymentsForSupplier.reduce(
          (sum, payment) => sum + (parseFloat(payment.amount) || 0), 0
        );

        // Total payé aujourd'hui
        const paymentsToday = weeklyPaymentsForSupplier.filter(p => {
          const paymentDate = new Date(p.date).toISOString().split('T')[0];
          return paymentDate === date;
        });

        const totalPaidToday = paymentsToday.reduce(
          (sum, payment) => sum + (parseFloat(payment.amount) || 0), 0
        );

        const expenseAmount = parseFloat(expense.amount) || 0;
        const previouslyPaid = parseFloat(expense.previouslyPaid) || 0;
        const totalPaidSoFar = previouslyPaid + totalPaidThisWeek;
        const remainingAmount = Math.max(0, expenseAmount - totalPaidSoFar);

        return {
          ...expense,
          amount: expenseAmount,
          previouslyPaid: previouslyPaid,
          totalPaidThisWeek: parseFloat(totalPaidThisWeek.toFixed(2)),
          totalPaidToday: parseFloat(totalPaidToday.toFixed(2)),
          totalPaidSoFar: parseFloat(totalPaidSoFar.toFixed(2)),
          remainingAmount: parseFloat(remainingAmount.toFixed(2)),
          isFullyPaid: remainingAmount <= 0.01,
          weeklyPayments: weeklyPaymentsForSupplier
        };
      });

      // 10. Initialiser les inputs
      const initialInputs = {};
      expensesWithUpdatedAmounts.forEach(expense => {
        initialInputs[expense._id] = "";
      });
      setExpenseInputs(initialInputs);

      const initialSupplierInputs = {};
      supplierExpensesWithAmounts.forEach(expense => {
        initialSupplierInputs[expense._id] = "";
      });
      setSupplierInputs(initialSupplierInputs);

      // 11. Calculer le surplus espèces RÉEL
      const realSurplus = Math.max(0, lastClosureRevenue.espece - totalAllocatedToday);

      // 12. Charger le résumé hebdomadaire POUR LA LOCATION
      const weeklySummaryData = await calculateWeeklySummary(date, effectiveLocation);

      // Dépenses payées = somme(PAYÉ CETTE SEMAINE régulières) + somme(PAYÉ CETTE SEMAINE fournisseurs)
      const paidRegulieres = expensesWithUpdatedAmounts.reduce((s, e) => s + (parseFloat(e.previouslyPaid) || 0), 0);
      const paidFournisseurs = supplierExpensesWithAmounts.reduce((s, e) => s + (parseFloat(e.totalPaidThisWeek) || 0), 0);
      const totalExpensesPaid = paidRegulieres + paidFournisseurs;

      const baseSummary = weeklySummaryData || {
        totalRevenue: 0,
        totalExpensesPaid: 0,
        totalNetProfit: 0,
        remainingExpenses: 0,
        profitMargin: 0
      };
      const netProfit = (baseSummary.totalRevenue || 0) - totalExpensesPaid;
      const profitMargin = (baseSummary.totalRevenue || 0) > 0
        ? (netProfit / (baseSummary.totalRevenue || 0)) * 100
        : 0;

      // 13. Mettre à jour le state en UNE SEULE FOIS
      setPerformanceData({
        date,
        lastClosureRevenue,
        expenses: expensesWithUpdatedAmounts,
        allocations: existingAllocations,
        supplierPayments: supplierExpensesWithAmounts,
        weeklySupplierPayments,
        dailyPerformance: {
          revenue: lastClosureRevenue.total,
          expensesPaid: totalAllocatedToday,
          netProfit: lastClosureRevenue.total - totalAllocatedToday,
          profitMargin: lastClosureRevenue.total > 0 ?
            ((lastClosureRevenue.total - totalAllocatedToday) / lastClosureRevenue.total * 100) : 0
        },
        weeklySummary: {
          ...baseSummary,
          totalExpensesPaid,
          totalNetProfit: netProfit,
          profitMargin
        },
        realSurplus
      });

      console.log(`✅ Données chargées avec succès pour ${userLocationName || "Toutes les locations"}`);

    } catch (err) {
      console.error("❌ Erreur chargement données:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 CORRIGÉ : Calculer le résumé hebdomadaire UNIQUEMENT pour la semaine en cours
  const calculateWeeklySummary = async (date, locationFilter = null) => {
    try {
      // 1. Obtenir la date du lundi de la semaine en cours
      const selectedDate = new Date(date);
      const dayOfWeek = selectedDate.getDay(); // 0=dimanche, 1=lundi, ..., 6=samedi

      // Calculer le lundi de la semaine (si dimanche = 0, on recule de 6 jours)
      const monday = new Date(selectedDate);
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      monday.setDate(selectedDate.getDate() + diffToMonday);
      monday.setHours(0, 0, 0, 0);

      // 2. Calculer le dimanche de la semaine en cours
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      // 3. Formater pour l'API
      const weekStartStr = monday.toISOString().split('T')[0];
      const weekEndStr = sunday.toISOString().split('T')[0];

      console.log("📅 Calcul résumé hebdo - Période:", {
        dateSélectionnée: date,
        jourSemaine: dayOfWeek,
        lundi: weekStartStr,
        dimanche: weekEndStr,
        plage: `${weekStartStr} → ${weekEndStr}`,
        locationFiltre: locationFilter || "toutes"
      });

      // Déterminer la location effective pour les requêtes
      // User: toujours sa location. Admin "toutes": null. Admin 1 location: locationFilter.
      const effectiveLocation = locationFilter !== null && locationFilter !== undefined
        ? (locationFilter === "all" ? null : locationFilter)
        : (!isAdmin && userlocation && userlocation !== "all" ? userlocation : null);

      // 4. Récupérer les clôtures — API attend locationId (pas location)
      const params = {};
      if (effectiveLocation && effectiveLocation !== "all") {
        params.locationId = effectiveLocation;
      }

      const allClosuresRes = await axiosInstance.get("/api/CloseDay/getAllClosures", { params });
      const allClosures = allClosuresRes.data || [];

      // 5. Filtrer MANUELLEMENT pour garder uniquement les clôtures de la semaine en cours
      const weeklyClosures = allClosures.filter(closure => {
        if (!closure.date) return false;

        const closureDate = new Date(closure.date);
        const closureDateStr = closureDate.toISOString().split('T')[0];

        // Vérifier si la date est entre lundi et dimanche (inclus)
        return closureDate >= monday && closureDate <= sunday;
      });

      // 6. Filtrer uniquement les clôtures "fin_journee"
      const finJourneeClosures = weeklyClosures.filter(c => c.closureType === "fin_journee");

      console.log(`📊 Résumé hebdo:`, {
        totalClosures: allClosures.length,
        closuresCetteSemaine: weeklyClosures.length,
        finJourneeCetteSemaine: finJourneeClosures.length,
        location: effectiveLocation || "toutes",
        userType: isAdmin ? "admin" : "user"
      });

      // 7. Calculer les recettes de la semaine
      const totalRevenue = finJourneeClosures.reduce((sum, closure) => {
        let closureTotal = 0;

        // Vérifier totalSales d'abord
        if (closure.totalSales !== undefined && closure.totalSales !== null) {
          const sales = parseFloat(closure.totalSales);
          if (!isNaN(sales)) {
            closureTotal = sales;
          }
        }
        // Sinon calculer depuis paymentStats
        else if (closure.paymentStats && typeof closure.paymentStats === 'object') {
          closureTotal = Object.values(closure.paymentStats)
            .reduce((s, val) => s + (parseFloat(val) || 0), 0);
        }

        return sum + closureTotal;
      }, 0);

      // 8. Récupérer les allocations de la semaine en cours
      const allocParams = {
        startDate: weekStartStr,
        endDate: weekEndStr
      };

      if (effectiveLocation && effectiveLocation !== "all") {
        allocParams.locationId = effectiveLocation;
      }

      const regularAllocationsRes = await axiosInstance.get("/api/Allocations/getWeeklyAllocations", { params: allocParams });
      const weeklyRegularAllocations = regularAllocationsRes.data?.allocations || [];

      // 9. Récupérer les paiements fournisseurs de la semaine en cours
      const supplierParams = {
        startDate: weekStartStr,
        endDate: weekEndStr
      };

      if (effectiveLocation && effectiveLocation !== "all") {
        supplierParams.location = effectiveLocation;
      }

      const supplierPaymentsRes = await axiosInstance.get("/api/SupplierPayments/getWeeklySummary", { params: supplierParams });
      const weeklySupplierPayments = supplierPaymentsRes.data?.weeklyPayments || [];

      // 10. Calculer les dépenses payées cette semaine
      const regularExpensesPaid = weeklyRegularAllocations.reduce((sum, alloc) =>
        sum + (parseFloat(alloc.amount) || 0), 0
      );

      const supplierExpensesPaid = weeklySupplierPayments.reduce((sum, payment) =>
        sum + (parseFloat(payment.amount) || 0), 0
      );

      const totalExpensesPaid = regularExpensesPaid + supplierExpensesPaid;

      // 11. Calculer le bénéfice net
      const totalNetProfit = totalRevenue - totalExpensesPaid;
      const profitMargin = totalRevenue > 0 ? (totalNetProfit / totalRevenue) * 100 : 0;

      const data = {
        totalRevenue: parseFloat(totalRevenue.toFixed(2)),
        totalExpensesPaid: parseFloat(totalExpensesPaid.toFixed(2)),
        totalNetProfit: parseFloat(totalNetProfit.toFixed(2)),
        remainingExpenses: 0,
        profitMargin: parseFloat(profitMargin.toFixed(1)),
        weekRange: `${formatDateDisplay(weekStartStr)} au ${formatDateDisplay(weekEndStr)}`,
        details: {
          nombreClotures: finJourneeClosures.length,
          periode: `${weekStartStr} → ${weekEndStr}`,
          location: effectiveLocation || "toutes"
        }
      };

      console.log("✅ Résumé hebdo final:", data);
      return data;

    } catch (err) {
      console.error("❌ Erreur calcul résumé hebdo:", err);
      return {
        totalRevenue: 0,
        totalExpensesPaid: 0,
        totalNetProfit: 0,
        remainingExpenses: 0,
        profitMargin: 0,
        weekRange: "Erreur de calcul"
      };
    }
  };

  // Fonction pour obtenir la date du dimanche de la semaine
  const getWeekEndDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDay(); // 0=dimanche, 1=lundi, etc.

    if (day === 0) {
      // C'est déjà dimanche
      return date.toISOString().split('T')[0];
    }

    // Calculer le prochain dimanche
    const diffToSunday = 7 - day;
    const sunday = new Date(date);
    sunday.setDate(date.getDate() + diffToSunday);
    sunday.setHours(23, 59, 59, 999);

    return sunday.toISOString().split('T')[0];
  };

  // 🔥 Données par défaut pour les dépenses
  const getDefaultExpenses = () => {
    return [
      {
        _id: "1",
        name: "Salaires du personnel",
        category: "salaires",
        amount: 5000,
        priority: 1,
        previouslyPaid: 0
      },
      {
        _id: "2",
        name: "Électricité (STEG)",
        category: "electricite",
        amount: 800,
        priority: 2,
        previouslyPaid: 0
      }
    ];
  };

  const openKeypad = (type, id, title) => {
    if (loading) return;
    setKeypad({ show: true, type, id, title });
  };
  const closeKeypad = () => setKeypad((k) => ({ ...k, show: false }));
  const confirmKeypad = (value) => {
    if (keypad.type === "expense" && keypad.id) handleAllocationChange(keypad.id, value);
    if (keypad.type === "supplier" && keypad.id) handleSupplierPaymentChange(keypad.id, value);
    closeKeypad();
  };

  // 🔥 CORRIGÉ: Gérer le changement d'allocation pour dépenses régulières
  const handleAllocationChange = (expenseId, value) => {
    const expense = performanceData.expenses.find(e => e._id === expenseId);
    if (!expense) return;

    const numValue = value === "" ? "" : parseFloat(value) || 0;

    const remainingAmount = parseFloat(expense.remainingAmount) || 0;
    if (numValue !== "" && numValue > remainingAmount) {
      alert(`Le montant ne peut pas dépasser ${formatCurrency(remainingAmount)}`);
      return;
    }

    // Vérifier le total avec les allocations existantes
    const newTotalAllocated = calculateTotalAllocatedWithNewValue(expenseId, numValue);
    const especeAvailable = performanceData.lastClosureRevenue.espece;
    const alreadyAllocatedToday = calculateTotalAllocatedToday();

    if (newTotalAllocated > (especeAvailable - alreadyAllocatedToday)) {
      alert(`Le total alloué (${formatCurrency(newTotalAllocated)}) dépasse les espèces disponibles après allocations existantes (${formatCurrency(especeAvailable - alreadyAllocatedToday)})`);
      return;
    }

    setExpenseInputs(prev => ({
      ...prev,
      [expenseId]: numValue
    }));
  };

  // 🔥 CORRIGÉ: Calculer les fonds disponibles pour les fournisseurs
  const calculateAvailableSupplierFunds = (supplierId = null) => {
    // 1. Total des fonds hors espèces
    const nonEspeceTotal = performanceData.lastClosureRevenue.tpe +
      performanceData.lastClosureRevenue.ticketResto +
      performanceData.lastClosureRevenue.glovo +
      performanceData.lastClosureRevenue.yassir;

    // 2. Surplus espèces réel (après allocations sauvegardées)
    const surplus = performanceData.realSurplus || calculateRealSurplus();

    // 3. Total des fonds disponibles
    const totalAvailableFunds = nonEspeceTotal + Math.max(0, surplus);

    // 4. Soustraction des paiements déjà planifiés aujourd'hui (sauvegardés)
    const totalPaidToday = performanceData.supplierPayments.reduce(
      (sum, supplier) => sum + (parseFloat(supplier.totalPaidToday) || 0), 0
    );

    // 5. Calcul des fonds disponibles BRUTS
    const availableFundsBrut = Math.max(0, totalAvailableFunds - totalPaidToday);

    // Si on demande pour un fournisseur spécifique
    if (supplierId) {
      return availableFundsBrut;
    }

    return availableFundsBrut;
  };

  // 🔥 NOUVELLE FONCTION: Vérifier si un montant dépasse les fonds disponibles (avec tolérance)
  const checkIfAmountExceedsFunds = (supplierId, value) => {
    // Si valeur vide ou nulle, pas d'erreur
    if (!value || value === "" || parseFloat(value) <= 0) {
      return false;
    }

    const numValue = parseFloat(value);

    // Récupérer tous les inputs actuels SAUF celui qu'on est en train de modifier
    let totalOtherInputs = 0;
    Object.entries(supplierInputs).forEach(([id, inputValue]) => {
      if (id !== supplierId && inputValue && inputValue !== "") {
        totalOtherInputs += parseFloat(inputValue) || 0;
      }
    });

    // Ajouter la valeur que l'utilisateur est en train de saisir
    const totalWithCurrentInput = totalOtherInputs + numValue;

    // Fonds disponibles (sans déduire les inputs en cours)
    const availableFunds = calculateAvailableSupplierFunds();

    // Comparer
    return totalWithCurrentInput > availableFunds;
  };

  // 🔥 CORRIGÉ: Gérer le changement de paiement pour fournisseurs (version améliorée)
  const handleSupplierPaymentChange = (supplierId, value) => {
    const supplierExpense = performanceData.supplierPayments.find(e => e._id === supplierId);
    if (!supplierExpense) return;

    const numValue = value === "" ? "" : parseFloat(value) || 0;

    // Vérification 1: Ne pas dépasser le montant restant
    const remainingAmount = parseFloat(supplierExpense.remainingAmount) || 0;
    if (numValue !== "" && numValue > remainingAmount) {
      alert(`Le montant ne peut pas dépasser ${formatCurrency(remainingAmount)}`);
      return;
    }

    // Mettre à jour l'input IMMÉDIATEMENT (permet la saisie complète)
    setSupplierInputs(prev => ({
      ...prev,
      [supplierId]: numValue === "" ? "" : value // Garder la valeur string pour permettre la saisie
    }));

    // NE PAS vérifier les fonds ici - laisser l'utilisateur finir de taper
    // La vérification se fera uniquement au moment de la sauvegarde
  };

  // 🔥 Calculer le total alloué avec une nouvelle valeur
  const calculateTotalAllocatedWithNewValue = (expenseId, newValue) => {
    let total = 0;

    Object.entries(expenseInputs).forEach(([id, value]) => {
      if (id === expenseId) {
        total += newValue !== "" ? parseFloat(newValue) : 0;
      } else {
        total += value !== "" ? parseFloat(value) : 0;
      }
    });

    return total;
  };

  // 🔥 Calculer le total des paiements fournisseurs avec une nouvelle valeur
  const calculateTotalSupplierPaymentsWithNewValue = (supplierId, newValue) => {
    let total = 0;

    Object.entries(supplierInputs).forEach(([id, value]) => {
      if (id === supplierId) {
        total += newValue !== "" ? parseFloat(newValue) : 0;
      } else {
        total += value !== "" ? parseFloat(value) : 0;
      }
    });

    return total;
  };

  // 🔥 CORRIGÉ: Valider les allocations pour dépenses régulières
  const validateAllocations = () => {
    const allocationsToSave = [];
    let totalAllocated = 0;

    performanceData.expenses.forEach(expense => {
      const amount = expenseInputs[expense._id];
      if (amount && amount > 0) {
        allocationsToSave.push({
          expenseId: expense._id,
          amount: parseFloat(amount)
        });
        totalAllocated += parseFloat(amount);
      }
    });

    if (allocationsToSave.length === 0) {
      alert("Aucune allocation à sauvegarder. Veuillez saisir des montants.");
      return null;
    }

    // Vérifications
    for (const allocation of allocationsToSave) {
      const expense = performanceData.expenses.find(e => e._id === allocation.expenseId);
      if (expense && allocation.amount > expense.remainingAmount) {
        alert(`Erreur : L'allocation pour "${expense.name}" (${allocation.amount.toFixed(2)} DT) dépasse le montant restant (${expense.remainingAmount.toFixed(2)} DT)`);
        return null;
      }
    }

    const especeAvailable = performanceData.lastClosureRevenue.espece;
    const alreadyAllocatedToday = calculateTotalAllocatedToday();

    if (totalAllocated > (especeAvailable - alreadyAllocatedToday)) {
      alert(`Erreur : Vous ne pouvez pas allouer plus (${totalAllocated.toFixed(2)} DT) que le montant espèces disponible après allocations existantes (${(especeAvailable - alreadyAllocatedToday).toFixed(2)} DT)`);
      return null;
    }

    return { allocationsToSave, totalAllocated };
  };

  // 🔥 CORRIGÉ: Valider les paiements fournisseurs

  const validateSupplierPayments = () => {
    const paymentsToSave = [];
    let totalPayments = 0;

    performanceData.supplierPayments.forEach(supplierExpense => {
      const amount = supplierInputs[supplierExpense._id];
      if (amount && amount > 0) {
        const numAmount = parseFloat(amount);
        paymentsToSave.push({
          expenseId: supplierExpense._id,
          amount: numAmount
        });
        totalPayments += numAmount;
      }
    });

    if (paymentsToSave.length === 0) {
      alert("Aucun paiement fournisseur à sauvegarder. Veuillez saisir des montants.");
      return null;
    }

    // Vérifications
    for (const payment of paymentsToSave) {
      const supplierExpense = performanceData.supplierPayments.find(e => e._id === payment.expenseId);
      if (supplierExpense && payment.amount > supplierExpense.remainingAmount) {
        alert(`Erreur : Le paiement pour "${supplierExpense.name}" (${payment.amount.toFixed(2)} DT) dépasse le montant restant (${supplierExpense.remainingAmount.toFixed(2)} DT)`);
        return null;
      }
    }

    // 🔥 VÉRIFICATION FINALE des fonds (seulement ici, pas pendant la saisie)
    const availableFunds = calculateAvailableSupplierFunds();
    if (totalPayments > availableFunds) {
      alert(`Erreur : Vous ne pouvez pas payer plus (${totalPayments.toFixed(2)} DT) que les fonds disponibles (${formatCurrency(availableFunds)})`);
      return null;
    }

    return { paymentsToSave, totalPayments };
  };

  // 🔥 CORRIGÉ: Sauvegarder les allocations pour dépenses régulières AVEC LOCATION
  const saveAllocations = async () => {
    const validation = validateAllocations();
    if (!validation) return;

    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const { allocationsToSave, totalAllocated } = validation;

      const allocationData = {
        date: selectedDate,
        allocations: allocationsToSave,
        dailyRevenue: {
          ...performanceData.lastClosureRevenue,
          total: performanceData.lastClosureRevenue.espece
        },
        location: userlocation === "all" ? null : userlocation,
        userId: user?._id,
        userName: user?.name || "Utilisateur"
      };

      console.log("📤 Envoi des allocations:", allocationData);

      const response = await axiosInstance.post("/api/Allocations/createAllocations", allocationData);

      if (response.data.success) {
        const surplus = performanceData.lastClosureRevenue.espece - (calculateTotalAllocatedToday() + totalAllocated);

        setSuccessMessage(`✅ ${response.data.summary.totalAllocations} allocations sauvegardées pour un total de ${response.data.summary.totalAmount.toFixed(2)} DT`);

        if (surplus > 0) {
          setSuccessMessage(prev => prev + ` - 💰 Nouveau surplus espèces: ${formatCurrency(surplus)}`);
        }

        // Recharger les données pour actualiser les montants
        await loadPerformanceData(selectedDate, userlocation === "all" ? null : userlocation);

        // Réinitialiser les inputs
        const resetInputs = {};
        performanceData.expenses.forEach(expense => {
          resetInputs[expense._id] = "";
        });
        setExpenseInputs(resetInputs);

        await calculateWeeklySummary(selectedDate, userlocation === "all" ? null : userlocation);

      } else {
        setError(response.data.message || "Erreur lors de la sauvegarde");
      }

    } catch (err) {
      console.error("❌ Erreur sauvegarde allocations:", err);
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 CORRIGÉ: Sauvegarder les paiements fournisseurs AVEC LOCATION
  const saveSupplierPayments = async () => {
    const validation = validateSupplierPayments();
    if (!validation) return;

    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const { paymentsToSave, totalPayments } = validation;

      const paymentData = {
        date: selectedDate,
        payments: paymentsToSave,
        availableFunds: calculateAvailableSupplierFunds(),
        location: userlocation === "all" ? null : userlocation,
        userId: user?._id,
        userName: user?.name || "Utilisateur",
        weekStartDate: weekStartDate
      };

      console.log("📤 Envoi des paiements fournisseurs:", paymentData);

      const response = await axiosInstance.post("/api/SupplierPayments/createPayments", paymentData);

      if (response.data.success) {
        const remainingFunds = calculateAvailableSupplierFunds() - totalPayments;

        setSuccessMessage(`✅ ${response.data.summary.totalPayments} paiements fournisseurs sauvegardés pour un total de ${response.data.summary.totalAmount.toFixed(2)} DT`);

        if (remainingFunds > 0) {
          setSuccessMessage(prev => prev + ` - 💰 Fonds restants: ${formatCurrency(remainingFunds)}`);
        }

        // Recharger les données pour actualiser les montants
        await loadPerformanceData(selectedDate, userlocation === "all" ? null : userlocation);

        // Réinitialiser les inputs
        const resetInputs = {};
        performanceData.supplierPayments.forEach(supplierExpense => {
          resetInputs[supplierExpense._id] = "";
        });
        setSupplierInputs(resetInputs);

        await calculateWeeklySummary(selectedDate, userlocation === "all" ? null : userlocation);

      } else {
        setError(response.data.message || "Erreur lors de la sauvegarde");
      }

    } catch (err) {
      console.error("❌ Erreur sauvegarde paiements fournisseurs:", err);
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 Charger les données initiales
  useEffect(() => {
    // Wait for user to be available
    if (!user) return;

    // For non-admins with a location, wait for userlocation to be initialized
    // The userlocation useEffect runs after this one, so we need to wait
    // Check: if user is not admin AND has localisation AND userlocation is still null, wait
    const hasLocation = user.localisation && (user.localisation._id || user.localisation);
    const shouldWaitForLocation = !isAdmin && hasLocation && !userlocation;

    if (shouldWaitForLocation) {
      // userlocation is still being initialized by the other useEffect, wait for it
      console.log("⏳ En attente de l'initialisation de la localisation...");
      return;
    }

    // Load data once:
    // - Admin users (isAdmin = true, userlocation can be null or "all")
    // - Non-admin users with location set (userlocation !== null)
    // - Non-admin users without location (userlocation === null, but user.localisation is also null/undefined)
    console.log(`🚀 Chargement des données pour la date: ${selectedDate}, Location: ${userlocation || userLocationName || "Toutes"}`);

    // Passer la location effective à loadPerformanceData
    const effectiveLocation = userlocation === "all" ? null : userlocation;
    loadPerformanceData(selectedDate, effectiveLocation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, user, userlocation, isAdmin, userLocationName]);

  // 🔥 Réinitialiser les allocations
  const resetAllocations = () => {
    const resetInputs = {};
    performanceData.expenses.forEach(expense => {
      resetInputs[expense._id] = "";
    });
    setExpenseInputs(resetInputs);
    setSuccessMessage("");
    setError("");
  };

  // 🔥 Réinitialiser les paiements fournisseurs
  const resetSupplierPayments = () => {
    const resetInputs = {};
    performanceData.supplierPayments.forEach(supplierExpense => {
      resetInputs[supplierExpense._id] = "";
    });
    setSupplierInputs(resetInputs);
    setSuccessMessage("");
    setError("");
  };

  // 🔥 Obtenir le nom du jour
  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[date.getDay()];
  };

  // 🔥 Calculer le total alloué (prévisualisation)
  const calculateTotalAllocated = () => {
    return Object.values(expenseInputs).reduce((sum, val) => {
      if (val === "" || val === undefined || val === null) return sum;
      return sum + (parseFloat(val) || 0);
    }, 0);
  };

  // 🔥 Calculer le total des paiements fournisseurs (prévisualisation)
  const calculateTotalSupplierPaymentsInput = () => {
    return Object.values(supplierInputs).reduce((sum, val) => {
      if (val === "" || val === undefined || val === null) return sum;
      return sum + (parseFloat(val) || 0);
    }, 0);
  };

  // 🔥 Calculer le total des dépenses restantes
  const calculateTotalRemainingExpenses = () => {
    return performanceData.expenses.reduce((sum, expense) =>
      sum + (parseFloat(expense.remainingAmount) || 0), 0
    );
  };

  // 🔥 Calculer le total des paiements fournisseurs restants
  const calculateTotalSupplierRemaining = () => {
    return performanceData.supplierPayments.reduce((sum, supplierExpense) =>
      sum + (parseFloat(supplierExpense.remainingAmount) || 0), 0
    );
  };

  // 🔥 Calculer le total alloué aujourd'hui (déjà sauvegardé)
  const calculateTotalAllocatedToday = () => {
    return performanceData.expenses.reduce((sum, expense) =>
      sum + (parseFloat(expense.totalAllocatedToday) || 0), 0
    );
  };

  // 🔥 Calculer le surplus espèces réel
  const calculateRealSurplus = () => {
    const totalAllocatedToday = calculateTotalAllocatedToday();
    return Math.max(0, performanceData.lastClosureRevenue.espece - totalAllocatedToday);
  };

  // 🔥 Obtenir le badge de catégorie
  const getCategoryBadge = (category) => {
    const categories = {
      salaires: { label: "SALAIRES", color: "#3498db" },
      electricite: { label: "ÉLECTRICITÉ", color: "#f39c12" },
      eau: { label: "EAU", color: "#3498db" },
      amenagement: { label: "AMÉNAGEMENT", color: "#9b59b6" },
      fournitures: { label: "FOURNITURES", color: "#00cec9" }
    };

    return categories[category] || { label: category.toUpperCase(), color: "#95a5a6" };
  };

  // 🔥 Formatage de date pour affichage
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // 🔥 Calculer le surplus espèces après allocations (prévision)
  const calculateEspeceSurplus = () => {
    const totalAllocated = calculateTotalAllocated();
    const alreadyAllocatedToday = calculateTotalAllocatedToday();
    return Math.max(0, performanceData.lastClosureRevenue.espece - (alreadyAllocatedToday + totalAllocated));
  };

  // 🔥 Calculer le total des paiements fournisseurs déjà sauvegardés aujourd'hui
  const calculateTotalSupplierPaymentsToday = () => {
    return performanceData.supplierPayments.reduce((sum, supplierExpense) =>
      sum + (parseFloat(supplierExpense.totalPaidToday) || 0), 0
    );
  };

  // 🔥 Calculer le total des paiements fournisseurs de la semaine
  const calculateTotalSupplierPaymentsThisWeek = () => {
    return performanceData.supplierPayments.reduce((sum, supplierExpense) =>
      sum + (parseFloat(supplierExpense.totalPaidThisWeek) || 0), 0
    );
  };

  // 🔥 Afficher la localisation courante
  const getCurrentLocationDisplay = () => {
    if (isAdmin) {
      if (userlocation === "all" || !userlocation) {
        return "🌍 ADMIN - Toutes les locations";
      } else {
        return `📍 ${userLocationName}`;
      }
    } else if (userLocationName) {
      return `📍 ${userLocationName}`;
    }
    return "📍 Localisation non définie";
  };

  return (
    <div className="performance-dashboard">
      {/* En-tête */}
      <div className="dashboard-header">
        <h1>📊 TABLEAU DE BORD DE PERFORMANCE</h1>
        <div className="header-info">
          <div className="date-selector">
            <label>Date :</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-input"
              max={new Date().toISOString().split('T')[0]}
            />
            <span className="day-name">{getDayName(selectedDate)}</span>
          </div>

          {isAdmin && (
            <div className="location-selector-admin">
              <label>📍 Filtrer par location :</label>
              <select
                value={userlocation || "all"}
                onChange={handleLocationChange}
                className="location-select"
              >
                <option value="all">🌍 Toutes les locations</option>
                {locations.map(loc => (
                  <option key={loc._id} value={loc._id}>{loc.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="user-info">
            <div className="location-display">
              {getCurrentLocationDisplay()}
            </div>
            <span className="user-role">{isAdmin ? "👑 ADMIN" : "👤 GÉRANT"}</span>
            {isAdmin && (
              <a href="/admin/expenses" className="admin-link">
                ⚙️ Gérer les charges
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
          <button onClick={() => setError("")}>Fermer</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage("")}>OK</button>
        </div>
      )}

      {/* SECTION : RECETTES DU JOUR */}
      <div className="daily-revenue-section">
        <h2>💰 RECETTES DU JOUR - {formatDateDisplay(performanceData.lastClosureRevenue.closureDate || selectedDate)}</h2>
        <div className="revenue-note">
          <em>Dernière journée clôturée (fin de journée) pour {userLocationName || "cette localisation"}</em>
          {performanceData.lastClosureRevenue.closureDate && (
            <span className="closure-info">
              (Clôture du {formatDateDisplay(performanceData.lastClosureRevenue.closureDate)} — {performanceData.lastClosureRevenue.closureType})
            </span>
          )}
          {!isAdmin && userLocationName && (
            <div className="location-info">
              <strong>📍 Localisation :</strong> {userLocationName}
            </div>
          )}
        </div>

        <div className="revenue-cards">
          <div className="revenue-card total">
            <div className="card-title">
              TOTAL RECETTES
              <div className="card-subtitle">
                Toutes méthodes confondues
              </div>
            </div>
            <div className="card-amount">
              {formatCurrency(performanceData.lastClosureRevenue.total)}
            </div>
          </div>

          <div className="revenue-card espece-highlight">
            <div className="card-title">
              ESPÈCES
              <div className="card-note">
                (Pour allocations dépenses régulières)
              </div>
            </div>
            <div className="card-amount">
              {formatCurrency(performanceData.lastClosureRevenue.espece)}
            </div>
            <div className="card-percentage">
              {performanceData.lastClosureRevenue.total > 0 ?
                `${((performanceData.lastClosureRevenue.espece / performanceData.lastClosureRevenue.total) * 100).toFixed(1)}%` :
                '0%'}
            </div>
          </div>

          <div className="revenue-card tpe">
            <div className="card-title">CARTE BANCAIRE</div>
            <div className="card-amount">
              {formatCurrency(performanceData.lastClosureRevenue.tpe)}
            </div>
            <div className="card-percentage">
              {performanceData.lastClosureRevenue.total > 0 ?
                `${((performanceData.lastClosureRevenue.tpe / performanceData.lastClosureRevenue.total) * 100).toFixed(1)}%` :
                '0%'}
            </div>
          </div>

          <div className="revenue-card resto">
            <div className="card-title">TICKET RESTO</div>
            <div className="card-amount">
              {formatCurrency(performanceData.lastClosureRevenue.ticketResto)}
            </div>
            <div className="card-percentage">
              {performanceData.lastClosureRevenue.total > 0 ?
                `${((performanceData.lastClosureRevenue.ticketResto / performanceData.lastClosureRevenue.total) * 100).toFixed(1)}%` :
                '0%'}
            </div>
          </div>
        </div>

        {(performanceData.lastClosureRevenue.glovo > 0 || performanceData.lastClosureRevenue.yassir > 0) && (
          <div className="other-revenues">
            {performanceData.lastClosureRevenue.glovo > 0 && (
              <div className="revenue-item">
                <span>Glovo:</span>
                <strong>{formatCurrency(performanceData.lastClosureRevenue.glovo)}</strong>
              </div>
            )}
            {performanceData.lastClosureRevenue.yassir > 0 && (
              <div className="revenue-item">
                <span>Yassir:</span>
                <strong>{formatCurrency(performanceData.lastClosureRevenue.yassir)}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tableau des Dépenses RÉGULIÈRES */}
      <div className="expenses-section espece-allocation">
        <h2>💵 RÉPARTITION DES ESPÈCES SUR LES DÉPENSES RÉGULIÈRES</h2>
        <div className="section-subtitle">
          <div className="summary-row">
            <span>Localisation : <strong>{userLocationName || "Toutes"}</strong></span>
            <span>Espèces disponibles : <strong>{formatCurrency(performanceData.lastClosureRevenue.espece)}</strong></span>
            <span>Déjà alloué aujourd'hui : <strong>{formatCurrency(calculateTotalAllocatedToday())}</strong></span>
            <span>À allouer (prévision) : <strong>{formatCurrency(calculateTotalAllocated())}</strong></span>
            <span>Surplus après allocations : <strong>{formatCurrency(calculateEspeceSurplus())}</strong></span>
          </div>
        </div>

        <div className="expenses-table-container">
          {loading && performanceData.expenses.length === 0 ? (
            <div className="loading-table">
              <p>Chargement des dépenses régulières...</p>
            </div>
          ) : performanceData.expenses.length === 0 ? (
            <div className="no-expenses">
              <p>Aucune dépense régulière configurée pour {userLocationName || "cette localisation"}.</p>
            </div>
          ) : (
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>CATÉGORIE</th>
                  <th>DÉPENSE</th>
                  <th>MONTANT TOTAL</th>
                  <th>RESTANT À PAYER</th>
                  <th>PAYÉ CETTE SEMAINE</th>
                  <th>ALLOCATION D'AUJOURD'HUI</th>
                  <th>ALLOCATION ESPÈCES</th>
                  <th>STATUT</th>
                </tr>
              </thead>
              <tbody>
                {performanceData.expenses.map((expense) => {
                  const categoryBadge = getCategoryBadge(expense.category);
                  const allocationAmount = expenseInputs[expense._id] || 0;

                  return (
                    <tr key={expense._id} className={expense.isFullyPaid ? "fully-paid" : ""}>
                      <td className="category-cell">
                        <span
                          className="category-badge"
                          style={{ backgroundColor: categoryBadge.color }}
                        >
                          {categoryBadge.label}
                        </span>
                      </td>
                      <td className="expense-name">{expense.name}</td>
                      <td className="amount-cell">{formatCurrency(expense.amount)}</td>
                      <td className="remaining-cell">
                        <strong>{formatCurrency(expense.remainingAmount)}</strong>
                      </td>
                      <td className="weekly-paid-cell">
                        <strong>{formatCurrency(expense.previouslyPaid)}</strong>
                      </td>
                      <td className="allocated-today-cell">
                        <strong className={expense.totalAllocatedToday > 0 ? "allocated-text" : ""}>
                          {expense.totalAllocatedToday > 0 ? formatCurrency(expense.totalAllocatedToday) : "-"}
                        </strong>
                      </td>
                      <td className="allocation-input-cell">
                        <div className="allocation-input-group">
                          <input
                            type="number"
                            min="0"
                            max={Math.min(expense.remainingAmount, performanceData.lastClosureRevenue.espece)}
                            step="0.01"
                            value={expenseInputs[expense._id] || ""}
                            onChange={(e) => handleAllocationChange(expense._id, e.target.value)}
                            className="allocation-input"
                            disabled={loading || expense.isFullyPaid}
                            placeholder="0.00"
                          />
                          <span className="currency-symbol">DT</span>
                          <button
                            type="button"
                            className="perf-keypad-btn"
                            onClick={() => openKeypad("expense", expense._id, `Allocation · ${expense.name}`)}
                            disabled={loading || expense.isFullyPaid}
                            title="Clavier numérique"
                            aria-label="Clavier numérique"
                          >
                            ⌨
                          </button>
                        </div>
                      </td>
                      <td className="status-cell">
                        <div className={`status-indicator ${expense.isFullyPaid ? 'paid' : 'pending'}`}>
                          {expense.isFullyPaid ? '✓ Payé' : 'À payer'}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                <tr className="total-row">
                  <td colSpan="2"><strong>TOTAUX</strong></td>
                  <td><strong>{formatCurrency(performanceData.expenses.reduce((a, e) => a + (e.amount || 0), 0))}</strong></td>
                  <td><strong>{formatCurrency(calculateTotalRemainingExpenses())}</strong></td>
                  <td><strong>{formatCurrency(performanceData.expenses.reduce((a, e) => a + (e.previouslyPaid || 0), 0))}</strong></td>
                  <td><strong>{formatCurrency(calculateTotalAllocatedToday())}</strong></td>
                  <td><strong>{formatCurrency(calculateTotalAllocated())}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <div className="allocation-actions">
          <button
            onClick={saveAllocations}
            className="save-btn"
            disabled={loading || calculateTotalAllocated() === 0}
          >
            {loading ? "⏳ Sauvegarde..." : "💾 Sauvegarder les allocations espèces"}
          </button>

          <button
            onClick={resetAllocations}
            className="reset-btn"
            disabled={loading}
          >
            🔄 Réinitialiser saisie
          </button>
        </div>
      </div>

      {/* Section PAIEMENTS FOURNISSEURS */}
      <div className="supplier-payments-section">
        <h2>🏢 PAIEMENTS FOURNISSEURS</h2>
        <div className="section-subtitle">
          <div className="summary-row">
            <span>Localisation : <strong>{userLocationName || "Toutes"}</strong></span>
            <span>Semaine du : <strong>{formatDateDisplay(weekStartDate)}</strong></span>
            <span>Fonds disponibles : <strong>{formatCurrency(calculateAvailableSupplierFunds())}</strong></span>
            <span>Déjà payé cette semaine : <strong>{formatCurrency(calculateTotalSupplierPaymentsThisWeek())}</strong></span>
            <span>Déjà payé aujourd'hui : <strong>{formatCurrency(calculateTotalSupplierPaymentsToday())}</strong></span>
          </div>
          <div className="summary-row">
            <span>À payer (prévision) : <strong>{formatCurrency(calculateTotalSupplierPaymentsInput())}</strong></span>
            <span className="highlight">Reste disponible : <strong>{formatCurrency(Math.max(0, calculateAvailableSupplierFunds() - calculateTotalSupplierPaymentsInput()))}</strong></span>
          </div>
          <div className="summary-note">
            ⚠️ Les paiements sont cumulés du lundi midi au dimanche soir. Réinitialisation chaque lundi à midi.
          </div>
        </div>

        <div className="supplier-payments-table-container">
          {loading && performanceData.supplierPayments.length === 0 ? (
            <div className="loading-table">
              <p>Chargement des paiements fournisseurs...</p>
            </div>
          ) : performanceData.supplierPayments.length === 0 ? (
            <div className="no-expenses">
              <p>Aucune dépense fournisseur trouvée pour {userLocationName || "cette localisation"}.</p>
            </div>
          ) : (
            <table className="supplier-payments-table">
              <thead>
                <tr>
                  <th>FOURNISSEUR</th>
                  <th>MONTANT TOTAL</th>
                  <th>DÉJÀ PAYÉ</th>
                  <th>RESTANT À PAYER</th>
                  <th>PAYÉ CETTE SEMAINE</th>
                  <th>PAYÉ AUJOURD'HUI</th>
                  <th>PAIEMENT À EFFECTUER</th>
                  <th>STATUT</th>
                </tr>
              </thead>
              <tbody>
                {performanceData.supplierPayments.map((supplierExpense) => {
                  const allocationAmount = supplierInputs[supplierExpense._id] || 0;

                  return (
                    <tr key={supplierExpense._id} className={supplierExpense.isFullyPaid ? "fully-paid" : ""}>
                      <td className="supplier-name-cell">
                        <div className="supplier-name">{supplierExpense.name}</div>
                        <div className="supplier-category">
                          <span className="category-badge" style={{ backgroundColor: "#e74c3c" }}>
                            FOURNISSEUR
                          </span>
                        </div>
                      </td>
                      <td className="amount-cell">{formatCurrency(supplierExpense.amount)}</td>
                      <td className="previously-paid-cell">
                        {formatCurrency(supplierExpense.previouslyPaid)}
                      </td>
                      <td className="remaining-cell">
                        <strong>{formatCurrency(supplierExpense.remainingAmount)}</strong>
                      </td>
                      <td className="weekly-paid-cell">
                        <strong>{formatCurrency(supplierExpense.totalPaidThisWeek)}</strong>
                      </td>
                      <td className="today-paid-cell">
                        <strong>{supplierExpense.totalPaidToday > 0 ? formatCurrency(supplierExpense.totalPaidToday) : "-"}</strong>
                      </td>
                      <td className="allocation-input-cell">
                        <div className="allocation-input-group">
                          <input
                            type="number"
                            min="0"
                            max={Math.min(supplierExpense.remainingAmount, calculateAvailableSupplierFunds(supplierExpense._id))}
                            step="0.01"
                            value={supplierInputs[supplierExpense._id] || ""}
                            onChange={(e) => handleSupplierPaymentChange(supplierExpense._id, e.target.value)}
                            className="allocation-input"
                            disabled={loading || supplierExpense.isFullyPaid}
                            placeholder="0.00"
                          />
                          <span className="currency-symbol">DT</span>
                          <button
                            type="button"
                            className="perf-keypad-btn"
                            onClick={() => openKeypad("supplier", supplierExpense._id, `Paiement · ${supplierExpense.name || "Fournisseur"}`)}
                            disabled={loading || supplierExpense.isFullyPaid}
                            title="Clavier numérique"
                            aria-label="Clavier numérique"
                          >
                            ⌨
                          </button>
                        </div>
                        <div className="available-funds">
                          Disponible: {formatCurrency(calculateAvailableSupplierFunds(supplierExpense._id))}
                        </div>
                      </td>
                      <td className="status-cell">
                        <div className={`status-indicator ${supplierExpense.isFullyPaid ? 'paid' : 'pending'}`}>
                          {supplierExpense.isFullyPaid ? '✓ Payé' : 'À payer'}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                <tr className="total-row">
                  <td colSpan="1"><strong>TOTAUX</strong></td>
                  <td><strong>{formatCurrency(performanceData.supplierPayments.reduce((a, e) => a + (e.amount || 0), 0))}</strong></td>
                  <td><strong>{formatCurrency(performanceData.supplierPayments.reduce((a, e) => a + (e.previouslyPaid || 0), 0))}</strong></td>
                  <td><strong>{formatCurrency(calculateTotalSupplierRemaining())}</strong></td>
                  <td><strong>{formatCurrency(calculateTotalSupplierPaymentsThisWeek())}</strong></td>
                  <td><strong>{formatCurrency(calculateTotalSupplierPaymentsToday())}</strong></td>
                  <td><strong>{formatCurrency(calculateTotalSupplierPaymentsInput())}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <div className="allocation-actions">
          <button
            onClick={saveSupplierPayments}
            className="save-btn"
            disabled={loading || calculateTotalSupplierPaymentsInput() === 0}
          >
            {loading ? "⏳ Sauvegarde..." : "💾 Sauvegarder les paiements fournisseurs"}
          </button>

          <button
            onClick={resetSupplierPayments}
            className="reset-btn"
            disabled={loading}
          >
            🔄 Réinitialiser saisie
          </button>
        </div>
      </div>

      {/* Résumé Hebdomadaire */}
      <div className="weekly-summary-section">
        <h2>📅 RÉSUMÉ HEBDOMADAIRE</h2>
        <div className="summary-info">
          <div className="location-info">
            {getCurrentLocationDisplay()}
          </div>
          <div className="week-range">
            Semaine du {formatDateDisplay(weekStartDate)} au {formatDateDisplay(getWeekEndDate(selectedDate))}
          </div>
        </div>

        <div className="weekly-cards">
          <div className="weekly-card">
            <div className="card-title">Recettes semaine</div>
            <div className="card-amount">{formatCurrency(performanceData.weeklySummary.totalRevenue)}</div>
            <div className="card-subtitle">
              {userlocation === "all" || !userlocation ? "Toutes les localisations" : userLocationName}
            </div>
            <div className="card-note">
              Total des recettes fin de journée (lundi au dimanche)
            </div>
          </div>

          <div className="weekly-card">
            <div className="card-title">Dépenses payées</div>
            <div className="card-amount">{formatCurrency(performanceData.weeklySummary.totalExpensesPaid)}</div>
            <div className="card-subtitle">
              {userlocation === "all" || !userlocation ? "Toutes localisations confondues" : userLocationName}
            </div>
            <div className="card-note">
              = Payé cette semaine (régulières) + Payé cette semaine (fournisseurs)
            </div>
          </div>

          <div className="weekly-card profit">
            <div className="card-title">Bénéfice net</div>
            <div className="card-amount">{formatCurrency(performanceData.weeklySummary.totalNetProfit)}</div>
            <div className="card-percentage">
              Marge : {performanceData.weeklySummary.profitMargin.toFixed(1)}%
            </div>
            <div className="card-subtitle">
              {performanceData.weeklySummary.totalNetProfit >= 0 ? "✅ Rentable" : "❌ Déficit"}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Chargement des données...</p>
        </div>
      )}

      <NumericKeypadModal
        show={keypad.show}
        title={keypad.title}
        value={
          keypad.type === "expense" && keypad.id
            ? expenseInputs[keypad.id] ?? ""
            : keypad.type === "supplier" && keypad.id
            ? supplierInputs[keypad.id] ?? ""
            : ""
        }
        onConfirm={confirmKeypad}
        onCancel={closeKeypad}
      />
    </div>
  );
}

export default PerformanceDashboard;